// ---- Cinematic puppet stage (plan 26) ----
// Throwaway actor rigs for cutscenes: built from the SAME factories as live
// characters (buildPlayer / buildNpc + dressNpc), driven per frame from the pure
// timeline's ActorPoses using the studio combat-driver's proven recipe —
// position + rotation assigned, walking derived from the frame-to-frame delta,
// gait + weapon-pose layered after. No nameplates, no colliders, no Live/net
// records — puppets are render-only and exist only while a cinematic plays.
// THREE-but-DOM-free (the letterbox/dialogue DOM lives in ui.ts).
//
// Boat-arrival extras (plan 26): an actor may FLOAT (rest on the water surface
// with a gentle buoyancy bob/roll + a bow-wake wash), RIDE another actor (its
// world pose composes on the vehicle's transform + a seat offset, gait
// suppressed — how riders stay in a moving boat), and hold a named POSE (a
// static stance layered over the idle each frame — slump/seated). All three are
// CLIENT-VISUAL render concerns: the pure timeline still emits each actor's
// x/z/ry, so the parity test + headless inspector are untouched.

import * as THREE from "three";
import type { CinematicDoc } from "../content/schemas.ts";
import type { TimelineOut } from "./timeline.ts";
import { NPCS } from "../npc/roster.ts";
import { specForKind } from "../content/load.ts";
import { buildNpc, type NpcSpec } from "../npc/define.ts";
import { dressNpc } from "../npc/dress.ts";
import { buildPlayer, updatePlayer } from "../player.ts";
import { animateGait, GAIT_WALK_SPEED, GAIT_RUN_SPEED, type GaitArchetype } from "../model/gait.ts";
import { collectRig, type RigHandle } from "../model/rig.ts";
import { applyWeaponPose, startRigAttack, equipRig } from "../anim/pose.ts";
import { applyBodyPose, snapBodyPose, type PoseRootOut } from "../anim/body-pose.ts";
import { modelDoc } from "../model/index.ts";
import { itemSpecOrNull } from "../items/index.ts";
import { disposeGroup } from "../meshkit.ts";
import { STEP_UP } from "../physics.ts";
import type { Appearance, Gear } from "../shared.ts";

/** What a host must provide for puppets to stand on its stage. */
export type CineStageCtx = {
  /** Where puppet groups mount (the scene, or a stage group). */
  parent: THREE.Object3D;
  /** Walkable height under (x,z) given the previous feet height.
   *  Game: physics surfaceAt over the stage world's surfaces; studio: terrain
   *  heightAt (no structure floors — documented v1 limitation). */
  surfaceY(x: number, z: number, prev: number): number;
  /** The active map's water surface height — where `float` actors rest. */
  waterY(): number;
  /** The viewer's own hero appearance — the "player" actor renders with it. */
  playerLookGear(): { look: number | Partial<Appearance>; gear: Gear };
  /** Display name for the "player" actor's dialogue lines. */
  playerName?(): string;
};

export type Puppet = {
  actorId: string;
  group: THREE.Group;
  rig: RigHandle | null;
  arch: GaitArchetype | null;
  isPlayer: boolean;
  /** Dialogue-bar display name (NPC kind's name / the hero's own). */
  name: string;
  feetY: number;
  lastX: number;
  lastZ: number;
  /** Rests on the water surface with a bob + wake (a boat). */
  float: boolean;
  /** Wake wash material (float actors) — opacity tracks speed. */
  wakeMat: THREE.Material | null;
  /** Carried by another actor: id + local seat offset + facing delta. */
  rideOf: string | null;
  seat: readonly [number, number, number] | null;
  faceDelta: number;
};

/** Gentle boat buoyancy (client-visual only — never fed back into the timeline). */
const BOB = { y: 0.055, roll: 0.028, pitch: 0.02, wy: 1.05, wr: 0.83, wp: 1.37 };

/** Reused seat-offset scratch — composeRide runs every frame per rider. */
const _seat = new THREE.Vector3();

/** A translucent wash that trails aft of a floating boat; opacity tracks speed. */
function makeWake(): { group: THREE.Group; mat: THREE.Material } {
  const group = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({
    color: 0xd6ecff, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide,
  });
  mat.userData.owned = true; // disposeGroup frees it with the puppet
  const m = new THREE.Mesh(new THREE.PlaneGeometry(0.85, 2.9), mat);
  m.rotation.x = -Math.PI / 2; // lie flat on the water surface
  m.position.set(0, 0.02, -2.7); // trailing aft of the stern (stern ≈ z -1.6)
  group.add(m);
  return { group, mat };
}

/** Resolve an npc KIND doc id to a spec: a placed spawn of that kind if one
 *  exists, else a synthetic template (cinematic-only kinds — a boat, a one-scene
 *  actor — are never placed in the world). */
function kindSpec(kind: string): NpcSpec | undefined {
  return NPCS.find((s) => (s.kind ?? s.id) === kind) ?? specForKind(kind);
}

/** Place a rider's world pose from its vehicle's transform + the seat offset,
 *  inheriting the boat's heading and bob tilt so riders ride the boat.
 *
 *  `ride.seat` is the FEET mark (e.g. boat floor under a thwart). Any live body
 *  pose's root drop (seated's `root.y`) layers ON TOP so pelvis lands on the
 *  seat surface without burying the feet — same contract as free-standing sits.
 *  Root-free poses (slump/rousing) leave the offset at 0 and ride.seat alone. */
function composeRide(p: Puppet, veh: Puppet): void {
  const [sx, sy, sz] = p.seat!;
  const local = _seat.set(sx, sy, sz).applyEuler(veh.group.rotation);
  p.group.position.set(
    veh.group.position.x + local.x,
    veh.group.position.y + local.y,
    veh.group.position.z + local.z,
  );
  p.group.rotation.set(veh.group.rotation.x, veh.group.rotation.y + p.faceDelta, veh.group.rotation.z);
  // Camera anchor stays at the feet mark; visual root drop is group.y only.
  p.feetY = p.group.position.y;
  p.lastX = p.group.position.x;
  p.lastZ = p.group.position.z;
  const pr = p.group.userData.poseRoot as PoseRootOut | undefined;
  if (pr) {
    p.group.position.y += pr.dy;
    p.group.rotation.x += pr.pitch;
    p.group.rotation.z += pr.roll;
  }
}

export function buildPuppets(doc: CinematicDoc, ctx: CineStageCtx): Map<string, Puppet> {
  const puppets = new Map<string, Puppet>();
  for (const actor of doc.actors) {
    const [x, z] = actor.at;
    let group: THREE.Group;
    let rig: RigHandle | null = null;
    let arch: GaitArchetype | null = null;
    let name: string;
    const isPlayer = actor.kind === "player";
    if (isPlayer) {
      const { look, gear } = ctx.playerLookGear();
      group = buildPlayer(look, gear);
      rig = (group.userData.parts as RigHandle) ?? collectRig(group);
      arch = "humanoid";
      name = ctx.playerName?.() ?? "You";
    } else {
      const base = kindSpec(actor.kind);
      if (!base) continue; // validation catches this; stay resilient at runtime
      // Clone + rebase like the skill-sim's sandboxCopy: puppet position is the
      // actor's mark; the id seeds deterministic per-actor variation.
      const spec: NpcSpec = { ...base, id: `cine-${doc.id}-${actor.id}`, x, y: 0, z, facing: actor.face ?? 0 };
      group = buildNpc(spec).group;
      dressNpc(group, spec);
      const archName = spec.model ? modelDoc(spec.model)?.archetype : undefined;
      arch = archName && archName !== "parts" ? (archName as GaitArchetype) : null;
      rig = arch === "humanoid" ? collectRig(group) : null;
      name = base.name;
    }
    if (actor.weapon && rig) {
      const item = itemSpecOrNull(actor.weapon);
      if (item) equipRig(group, rig, item);
    }
    // Spawn IN the starting pose at full weight — an actor authored slumped
    // shouldn't visibly sit down as the scene fades in.
    if (actor.pose && rig) snapBodyPose(group, rig, actor.pose, _poseOut);
    const float = actor.float ?? false;
    const feetY = float ? ctx.waterY() : ctx.surfaceY(x, z, 0);
    group.position.set(x, feetY, z);
    group.rotation.y = actor.face ?? 0;
    let wakeMat: THREE.Material | null = null;
    if (float) {
      const wake = makeWake();
      group.add(wake.group);
      wakeMat = wake.mat;
    }
    ctx.parent.add(group);
    puppets.set(actor.id, {
      actorId: actor.id,
      group,
      rig,
      arch,
      isPlayer,
      name,
      feetY,
      lastX: x,
      lastZ: z,
      float,
      wakeMat,
      rideOf: actor.ride?.of ?? null,
      seat: actor.ride?.seat ?? null,
      faceDelta: actor.ride?.face ?? 0,
    });
  }
  return puppets;
}

const _poseOut: PoseRootOut = { dy: 0, pitch: 0, roll: 0 };

/** Animate one puppet's BODY (limbs) for the given motion, then layer its hold
 *  pose — the SHARED body-pose system (content/anim/clips.json `poses`), so
 *  pose→pose beats (slump→rousing→seated) cross-fade instead of popping. Does
 *  NOT set the root position — the caller owns placement (terrain follow,
 *  float, or ride composition). A FREE actor's pose root folds into the
 *  returned bob + rotation.x/z; a RIDER stashes the root on `userData.poseRoot`
 *  for composeRide (feet at ride.seat + root drop on top). Returns the
 *  vertical offset to add over feetY (free actors only). */
function animateBody(p: Puppet, dt: number, t: number, moving: boolean, running: boolean, poseName: string | null): number {
  let bob = 0;
  // The pose pass reads userData.bodyPose — set it BEFORE updatePlayer, which
  // runs applyBodyPose itself (the player.ts host hook).
  p.group.userData.bodyPose = poseName;
  if (p.isPlayer) {
    bob = updatePlayer(p.group, dt, moving, t, running);
    // updatePlayer already wrote userData.poseRoot; free actors integrate it
    // here, riders leave it for composeRide.
    const pr = p.group.userData.poseRoot as PoseRootOut | undefined;
    if (pr && !p.rideOf) {
      bob += pr.dy;
      p.group.rotation.x = pr.pitch;
      p.group.rotation.z = pr.roll;
    }
  } else if (p.arch) {
    const ud = p.group.userData;
    ud.studioMoving = moving;
    ud.studioRunning = running;
    animateGait(p.arch, p.group, dt, 0, { root: false });
    if (p.rig) {
      applyWeaponPose(p.group, p.rig, dt);
      applyBodyPose(p.group, p.rig, dt, _poseOut); // zeroes _poseOut when idle
      // Mirror the player's poseRoot stash so composeRide is host-agnostic.
      ud.poseRoot = { dy: _poseOut.dy, pitch: _poseOut.pitch, roll: _poseOut.roll };
      if (!p.rideOf) {
        bob += _poseOut.dy;
        p.group.rotation.x = _poseOut.pitch;
        p.group.rotation.z = _poseOut.roll;
      }
    }
  }
  return bob;
}

/** Apply one timeline tick's poses onto the rigs (the combat-driver recipe).
 *  Two passes: vehicles/free actors first, then riders compose on their (now
 *  updated) vehicle so a rider is never a frame behind the boat it sits in. */
export function drivePuppets(
  puppets: Map<string, Puppet>,
  out: TimelineOut,
  dt: number,
  t: number,
  ctx: CineStageCtx,
): void {
  // Pass 1 — everything not being carried (boats, walkers, standers).
  for (const p of puppets.values()) {
    if (p.rideOf) continue;
    const pose = out.actors.get(p.actorId);
    if (!pose) continue;
    const speed = dt > 0 ? Math.hypot(pose.x - p.lastX, pose.z - p.lastZ) / dt : 0;
    p.lastX = pose.x;
    p.lastZ = pose.z;

    if (pose.gesture) {
      if (p.rig) startRigAttack(p.group, pose.gesture);
      else p.group.userData.studioAttack = true; // rigless bodies play the whole-group lunge
    }

    p.group.position.x = pose.x;
    p.group.position.z = pose.z;

    if (p.float) {
      // Rest on the water with a gentle bob + roll/pitch; keep the timeline heading.
      p.feetY = ctx.waterY();
      p.group.position.y = p.feetY + BOB.y * Math.sin(t * BOB.wy);
      p.group.rotation.set(BOB.pitch * Math.sin(t * BOB.wp), pose.ry, BOB.roll * Math.sin(t * BOB.wr));
      if (p.wakeMat) (p.wakeMat as THREE.MeshBasicMaterial).opacity = Math.min(0.4, speed * 0.13);
      continue; // a boat is a parts body — no gait
    }

    p.group.rotation.y = pose.ry;
    // Feet follow the stage surface: instant step-up (stairs), eased drops —
    // the game.ts local-player idiom.
    const targetY = ctx.surfaceY(pose.x, pose.z, p.feetY);
    const dy = targetY - p.feetY;
    p.feetY += dy > 0 ? Math.min(dy, STEP_UP) : dy * Math.min(1, dt * 10);
    const bob = animateBody(p, dt, t, speed > GAIT_WALK_SPEED, speed > GAIT_RUN_SPEED, pose.pose ?? null);
    p.group.position.y = p.feetY + bob;
  }

  // Pass 2 — riders: idle body (gait suppressed) + hold pose, composed onto the
  // vehicle's transform so they inherit its heading and bob.
  for (const p of puppets.values()) {
    if (!p.rideOf || !p.seat) continue;
    const pose = out.actors.get(p.actorId);
    if (pose?.gesture) {
      if (p.rig) startRigAttack(p.group, pose.gesture);
      else p.group.userData.studioAttack = true;
    }
    animateBody(p, dt, t, false, false, pose?.pose ?? null); // carried, not walking
    const veh = puppets.get(p.rideOf);
    if (veh) composeRide(p, veh);
  }
}

/** Resync internal motion state after a timeline jump (kills the one-frame
 *  teleport "sprint" the speed derivation would otherwise read). */
export function resyncPuppets(puppets: Map<string, Puppet>, out: TimelineOut, ctx: CineStageCtx): void {
  // Vehicles/free actors first, so riders can compose onto them.
  for (const p of puppets.values()) {
    if (p.rideOf) continue;
    const pose = out.actors.get(p.actorId);
    if (!pose) continue;
    p.lastX = pose.x;
    p.lastZ = pose.z;
    p.feetY = p.float ? ctx.waterY() : ctx.surfaceY(pose.x, pose.z, 0);
    p.group.position.set(pose.x, p.feetY, pose.z);
    p.group.rotation.y = pose.ry;
    // A jump lands in the target body pose at full weight (no sit-down ease
    // across a skip); the root offset applies immediately too.
    if (p.rig && snapBodyPose(p.group, p.rig, pose.pose ?? null, _poseOut)) {
      p.group.position.y += _poseOut.dy;
      p.group.rotation.x = _poseOut.pitch;
      p.group.rotation.z = _poseOut.roll;
    }
  }
  for (const p of puppets.values()) {
    if (!p.rideOf || !p.seat) continue;
    const pose = out.actors.get(p.actorId);
    // Joints + stash root; composeRide places feet at ride.seat then applies root.
    if (p.rig) {
      snapBodyPose(p.group, p.rig, pose?.pose ?? null, _poseOut);
      p.group.userData.poseRoot = { dy: _poseOut.dy, pitch: _poseOut.pitch, roll: _poseOut.roll };
    }
    const veh = puppets.get(p.rideOf);
    if (veh) composeRide(p, veh);
  }
}

export function disposePuppets(puppets: Map<string, Puppet>, ctx: CineStageCtx): void {
  for (const p of puppets.values()) {
    ctx.parent.remove(p.group);
    disposeGroup(p.group); // shared caches (toonMat / UNIT_BOX / SOFT_BOX) survive
  }
  puppets.clear();
}
